import * as colors from '@rezonant/colors/safe';

// Monkey patch console log to ensure we have a console (and all its features)
// if the browser/environment does not provide one

if (typeof window !== 'undefined') {
    let win = <any>window;

    if (typeof console === 'undefined') {
        win['console'] = {};
    }

    if (!console.log) console.log = function() { }
    if (!console.dir) console.dir = function() { }
    if (!console.error) console.error = function() { }

    if (!console.groupCollapsed) console.groupCollapsed = function() {
        console.log.apply(console, arguments);
    }

    if (!console.group) console.group = function() {
        console.log.apply(console, arguments);
    }

    if (!console.groupEnd) console.groupEnd = function() {}
} else {

    if (!console.groupCollapsed) console.groupCollapsed = function() {
        console.log.apply(console, arguments);
    }

    if (!console.group) console.group = function() {
        console.log.apply(console, arguments);
    }

    if (!console.groupEnd) console.groupEnd = function() {}
}

// Create a wrapper class which can correctly call all original console calls 

!function() {
    let originalConsole = console;
    let newConsole = Object.create(originalConsole);

    // Wrap all functions of console 

    Object.keys(originalConsole).forEach(key => {
        let method = originalConsole[key];

        // Don't wrap non-functions, allow prototypical inheritance on these.
        if (typeof method !== 'function')
            return;

        newConsole[key] = (...args) => originalConsole[key](...args);
    });

    if (typeof window !== 'undefined') {
        let win = <any>window;
        win['console'] = newConsole;
    } else {
        global['console'] = newConsole;
    }
}

export class Logger {

    // Before/After Log

    static beforeLogActions = [];
    static runBeforeLog(args) {
        this.beforeLogActions.forEach(x => x.apply(null, args));
    }

    static afterLogActions = [];
    static runAfterLog(args) {
        this.afterLogActions.forEach(x => x.apply(null, args));
    }

    static beforeLog(handler : (...args) => void) {
        this.beforeLogActions.push(handler);
    }

    static afterLog(handler : (...args) => void) {
        this.afterLogActions.push(handler);
    }

    // Before/After Dir

    static beforeDirActions = [];
    static runBeforeDir(args) {
        this.beforeDirActions.forEach(x => x.apply(null, args));
    }

    static afterDirActions = [];
    static runAfterDir(args) {
        this.afterDirActions.forEach(x => x.apply(null, args));
    }

    static beforeDir(handler : (...args) => void) {
        this.beforeDirActions.push(handler);
    }

    static afterDir(handler : (...args) => void) {
        this.afterDirActions.push(handler);
    }

    // Before/After Errors

    static beforeErrorActions = [];
    static runBeforeError(args) {
        this.beforeErrorActions.forEach(x => x.apply(null, args));
    }

    static afterErrorActions = [];
    static runAfterError(args) {
        this.afterErrorActions.forEach(x => x.apply(null, args));
    }

    static beforeError(handler : (...args) => void) {
        this.beforeErrorActions.push(handler);
    }

    static afterError(handler : (...args) => void) {
        this.afterErrorActions.push(handler);
    }

    /**
     * Header
     * @param message 
     */
    static header(message : string) {
        if (this.isServer)
            this.log(colors.underline(`${message}`));
        else
            this.log(`%c${message}`, 'font-size: 200%; font-family: sans-serif;');
    }

    static success(message : string) {
        if (this.isServer)
            this.log(colors.green(` ✓ ${message}`))
        else
            this.log(`%c✓ ${message}`, 'color: green;')
    }

    static importantSuccess(message : string) {
        if (this.isServer)
            this.log(colors.green(colors.bold(` ✓ ${message}`)))
        else
            this.log(`%c✓ ${message}`, 'color: green; font-weight: bold;')
    }

    static inspect(title : string, obj : any) {
        this.groupCollapsed(title);
        this.dir(obj);
        this.groupEnd();
    }

    static error(message : string, error : any) {
        this.criticalFailure(message);
        this.groupCollapsed('Error Details:');
        
        if (this.consoleSupports('error'))
            console.error(error);
        else 
            this.dir(error);
        
        this.groupEnd();
    }

    static get isServer() {
        return typeof window === 'undefined';
    }

    static failure(message : string) {
        if (this.isServer)
            this.log(colors.red(` ✗ ${message}`));
        else
            this.log(`%c✗ ${message}`, 'color: maroon;');
    }

    static panic(message : string, error : any = null) {
        this.log(`%cpanic`, 'font-size: 60pt;');
        this.error(message, error);
    }

    static criticalFailure(message : string) {
        if (this.isServer)
            this.log(colors.red(colors.bold(` ✗ ${message}`)));
        else
            this.log(`%c✗ ${message}`, 'color: maroon; font-weight: bold;');
    }

    private static groupDepth = 0;

    static consoleSupports(method : string) {
        return (typeof console !== 'undefined' && console[method]);
    }

    static get groupPrefix() {
        let prefix = '';
        for (let i = 0, max = this.groupDepth; i < max; ++i)
            prefix += '  ';
        return prefix;
    }

    static groupCollapsed(title : string) {
        if (this.isServer || !this.consoleSupports('groupCollapsed')) {
            this.log(`+ ${title}`);
            this.groupDepth += 1;
        } else {
            console.groupCollapsed(title);
        }
    }

    static groupEnd() {
        if (this.isServer || !this.consoleSupports('groupEnd')) {
            this.groupDepth -= 1;
        } else {
            console.groupEnd();
        }
    }

    static _styleSupported : boolean = null;
    static get styleSupported() : boolean {
        if (this._styleSupported !== null)
            return this._styleSupported;
        
        let supported = true;

        if (typeof navigator !== 'undefined') {
            if (navigator.userAgent.indexOf('Edge/') >= 0) {
                supported = false;
            }
        }

        return this._styleSupported = supported;
    }

    static dir(item : any) {
        if (this.consoleSupports('dir')) {
            console.dir(item);
        } else {
            this.log(JSON.stringify(item));
        }
    }
    static info(message : string) {
        if (this.isServer)
          this.log(colors.blue(` Ⓘ ${message}`));
        else
          this.log(`%cⒾ ${message}`, 'color: blue;');
    }

    static log(message : string, ...args) {
        if (!this.consoleSupports('log')) 
            return;

        if (message.startsWith('%c') && !this.styleSupported) {
            console.log(message.substr(2));
        } else {
            console.log(this.groupPrefix + message, ...args);
        }
    }
}